import { createContext, useContext, useMemo, useState } from "react";

import { DateRange, getDateRange } from "components/filters/dateRange";
import { useCurrentUserTimezone } from "utils/currentTimezone";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { controlHealthFiltersStorage } from "./storage";
import { ControlHealthFilters, DEFAULT_CONTROL_HEALTH_FILTERS } from "./types";

interface ControlHealthFiltersContextValue {
    filters: ControlHealthFilters;
    /** The preset resolved to absolute bounds; `null` for an incomplete custom range, which stops the fetch. */
    dateRange: DateRange | null;
    setFilters: (filters: ControlHealthFilters) => void;
    clearFilters: () => void;
}

const ControlHealthFiltersContext =
    createContext<ControlHealthFiltersContextValue | null>(null);

interface ControlHealthFiltersProviderProps {
    children: React.ReactNode;
}

export function ControlHealthFiltersProvider({
    children,
}: ControlHealthFiltersProviderProps) {
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const timezone = useCurrentUserTimezone();
    const [storedFilters, setStoredFilters] =
        useState<ControlHealthFilters>(readPersistedFilters);

    const value = useMemo(() => {
        // An org can lose Entity Management after a filter was persisted.
        const filters = controlEntitiesEnabled
            ? storedFilters
            : { ...storedFilters, entities: [] };

        const setFilters = (newFilters: ControlHealthFilters) => {
            setStoredFilters(newFilters);
            controlHealthFiltersStorage.set("filters", newFilters);
        };

        return {
            filters,
            dateRange: getDateRange(filters.dateRangePreset, {
                timezone,
                weekStartsOn: "monday",
            }),
            setFilters,
            clearFilters: () => setFilters(DEFAULT_CONTROL_HEALTH_FILTERS),
        };
    }, [controlEntitiesEnabled, storedFilters, timezone]);

    return (
        <ControlHealthFiltersContext.Provider value={value}>
            {children}
        </ControlHealthFiltersContext.Provider>
    );
}

export function useControlHealthFilters(): ControlHealthFiltersContextValue {
    const context = useContext(ControlHealthFiltersContext);
    if (context === null) {
        throw new Error(
            "useControlHealthFilters() must be used within a ControlHealthFiltersProvider"
        );
    }
    return context;
}

/** Merges with the defaults so an entry written by an older build stays usable. */
function readPersistedFilters(): ControlHealthFilters {
    return {
        ...DEFAULT_CONTROL_HEALTH_FILTERS,
        ...controlHealthFiltersStorage.getOr(
            "filters",
            DEFAULT_CONTROL_HEALTH_FILTERS
        ),
    };
}
